import { includes, get } from "lodash"
import createModule from "../createModule"
import view from "./view"
import { Creators } from "./model"
import { Creators as ProfileCreators } from "model/profileModal"
import { Creators as CompositeCreators } from "modules/analyze-composite-security/model"
import { getSortedContributors, getSortedGroupContributors, getSelectedGroupValue, getSelectedGroup, getAvailableRollups, getContributorsSummaryText, getExcelData, getGroupBy, getSelectedGroupSiblings, getIncludeNotHeld } from "./selectors"
import { getGroupsDropdownColumns } from "./selectors/group"
import { betasAreMissing } from "selectors/missingDataFiles"
import { getIsOpPro } from "modules/account-root/selectors"
import { generateClassificationQueryForContributors } from "helpers/classification"
import { getQueryGroupBy } from "helpers/rollups"
import { VIEW_TYPES } from "helpers/base"
import { convertPositionSetType } from "helpers/compositeSecurities"
import { getPage } from "helpers/compositeSecuritiesDetails"

export default createModule({
  view,
  externalProps: (state, props) => ({
    includeNotHeld: getIncludeNotHeld(state, props),
  }),
  required: ["account", "modelId", "positionSetId", "positionSetType", "missingDataFiles"],
  data: [{
    resource: "compositeBetaContributors",
    as: null,
    url: ({ positionSetId, positionSetType, modelId, selectedDate, viewType, expand, base, baseSignature, segment, missingDataFiles, classifications, includeNotHeld }) => {
      if (betasAreMissing(missingDataFiles, modelId)) { return null }
      if (selectedDate) {
        return {
          id: `compositeBetaContributors-${modelId}${positionSetId}${positionSetType}${selectedDate}${viewType}${expand}${baseSignature}${JSON.stringify(segment)}${includeNotHeld}`,
          query: `query ($modelId: String!, $positionSet: PositionSetInput!, $date: Date!, $base: [PositionSetInput], $includeNotHeld: Boolean) {
            model (id: $modelId) {
              simulation (positionSet: $positionSet, from: $date, to: $date, base: $base, interval: END_DATE) {
                betaContributors(includeNotHeld: $includeNotHeld) {
                  date
                  contributors {
                    id
                    assetClass
                    assetSubclass
                    country
                    notHeld
                    ${generateClassificationQueryForContributors("GICS", classifications)}
                    currency
                    description
                    percentEquity
                    bloombergTicker
                    modelProviderId
                    ${!includes(get(missingDataFiles, modelId), "predictedBeta") ? "predicted" : ""}
                    ${!includes(get(missingDataFiles, modelId), "historicalBeta") ? "historical" : ""}
                  }
                }
              }
            }
          }`,
          variables: {
            modelId,
            positionSet: {
              id: positionSetId,
              type: convertPositionSetType(positionSetType),
              segment,
              expand,
            },
            date: selectedDate,
            base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
            includeNotHeld,
          },
        }
      } else {
        return null
      }
    },
  }, {
    resource: "compositeGroupedBetaContributors",
    as: null,
    url: ({ positionSetId, positionSetType, modelId, selectedDate, viewType, expand, base, baseSignature, currentGrouping, segment, classifications, includeNotHeld }) => {
      if (!currentGrouping) {
        return null
      }

      const queryGroupBy = getQueryGroupBy(currentGrouping, viewType === VIEW_TYPES.ACTIVE)

      return {
        id: `compositeGroupedBetaContributors-${modelId}${positionSetId}${positionSetType}${selectedDate}${viewType}${expand}${baseSignature}${JSON.stringify(queryGroupBy)}${JSON.stringify(segment)}${includeNotHeld}`,
        query: `query ($modelId: String!, $positionSet: PositionSetInput!, $date: Date!, $base: [PositionSetInput], $groupBy: ContributorGroupType!, $groupById: String, $tier: String, $includeNotHeld: Boolean) {
            model (id: $modelId) {
              simulation (positionSet: $positionSet, from: $date, to: $date, base: $base, interval: END_DATE) {
                groupedBetaContributors (groupBy: $groupBy, groupById: $groupById, classificationTier: $tier, includeNotHeld: $includeNotHeld) {
                  date
                  groupedContributors {
                    name
                    id
                    totalPercentEquity
                    historical
                    predicted
                    contributors {
                      id
                      assetClass
                      country
                      notHeld
                      ${generateClassificationQueryForContributors("GICS", classifications)}
                      currency
                      description
                      percentEquity
                      historical
                      predicted
                      bloombergTicker
                      modelProviderId
                    }
                  }
                }
              }
            }
          }`,
        variables: {
          modelId,
          positionSet: {
            id: positionSetId,
            type: convertPositionSetType(positionSetType),
            segment,
            expand,
          },
          date: selectedDate,
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          includeNotHeld,
          ...queryGroupBy,
        },
      }
    },
  }],
  mapStateToProps: (state, props) => ({
    ...state.modules.betaContributors,
    data: getSortedContributors(state, props),
    groupedData: getSortedGroupContributors(state, props),
    availableRollups: getAvailableRollups(state, props),
    contributorsSummaryText: getContributorsSummaryText(state, props),
    excelData: getExcelData(state, props),
    isOpPro: getIsOpPro(state),
    groupBy: getGroupBy(state, props),
    selectedGroup: getSelectedGroup(state, props),
    selectedGroupValue: getSelectedGroupValue(state, props),
    selectedGroupSiblings: getSelectedGroupSiblings(state, props),
    groupsDropdownColumns: getGroupsDropdownColumns(state, props),
    page: getPage(state),
  }),
  actions: {
    ...Creators,
    openSecurityProfile: ProfileCreators.openSecurityProfile,
    openFactorProfile: ProfileCreators.openFactorProfile,
    openCompositeSecurity: CompositeCreators.openCompositeSecurity,
  },
})
